import React from 'react';
import { Button } from './button';
import { Icons } from './icons';
import { useTranslation } from 'react-i18next';
import { Sentry } from '@/lib/sentry';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  onError?: (err: Error) => void;
  fallback?: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error | undefined;
  eventId?: string | undefined; // ✅ NEW: Sentry event ID
};

// Separate functional component for error UI (can use hooks)
const ErrorBoundaryFallback = ({ error, eventId }: { error?: Error; eventId?: string }) => {
  const { t } = useTranslation('common');
  return (
    <div className='flex min-h-screen items-center justify-center bg-background p-4'>
      <div className='max-w-md rounded-lg border border-border bg-card p-6 text-center'>
        <h2 className='text-2xl font-bold text-foreground mb-4'>{t('routeError.title', 'Etwas ist schiefgelaufen')}</h2>
        <p className='text-sm text-muted-foreground mb-4'>{t('routeError.description', 'Ein unerwarteter Fehler ist aufgetreten. Unser Team wurde automatisch benachrichtigt.')}</p>

        {/* ✅ NEW: User feedback button */}
        {eventId && (
          <button
            className='mb-4 inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90'
            onClick={() => {
              Sentry.showReportDialog({
                eventId: eventId,
                lang: 'de',
                title: 'Fehler melden',
                subtitle: 'Helfen Sie uns, dieses Problem zu beheben',
                subtitle2: 'Beschreiben Sie bitte, was passiert ist',
                labelName: 'Name',
                labelEmail: 'E-Mail',
                labelComments: 'Was ist passiert?',
                labelClose: 'Schließen',
                labelSubmit: 'Senden',
                errorGeneric: 'Fehler beim Senden. Bitte versuchen Sie es erneut.',
                successMessage: 'Vielen Dank für Ihr Feedback!',
              });
            }}
          >
            Fehler melden
          </button>
        )}

        <button className='inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground' onClick={() => window.location.reload()}>
          {t('routeError.goHome', 'Seite neu laden')}
        </button>

        {import.meta.env.DEV && error && (
          <details className='mt-4 text-left'>
            <summary className='cursor-pointer text-sm font-medium text-muted-foreground'>{t('routeError.details', 'Technische Details (nur im Dev-Modus)')}</summary>
            <pre className='mt-2 overflow-auto text-xs text-destructive'>{error.stack}</pre>
          </details>
        )}
      </div>
    </div>
  );
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: undefined, eventId: undefined });
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.props.onError) this.props.onError(error);
    console.error('ErrorBoundary caught', error);

    // ✅ NEW: Send to Sentry and get event ID
    Sentry.withScope((scope) => {
      scope.setContext('react', {
        componentStack: errorInfo.componentStack,
      });
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <ErrorBoundaryFallback {...(this.state.error && { error: this.state.error })} {...(this.state.eventId && { eventId: this.state.eventId })} />;
    }
    return this.props.children as React.ReactElement;
  }
}

export default ErrorBoundary;

export const useCaptureRouteError = (error: Error) => {
  const capturedErrorRef = React.useRef<Error | null>(null);

  React.useEffect(() => {
    if (capturedErrorRef.current === error) {
      return;
    }

    capturedErrorRef.current = error;
    const routePath = window.location.pathname || 'unknown';
    Sentry.captureException(error, {
      tags: {
        error_boundary: 'tanstack_router',
        route_path: routePath,
        surface: 'frontend',
      },
      extra: {
        route_search: window.location.search,
      },
    });
  }, [error]);
};

export const RouteErrorComponent = ({ error, reset }: { error: Error; reset?: () => void }) => {
  const { t } = useTranslation('common');
  useCaptureRouteError(error);

  return (
    <div className='min-h-screen flex items-center justify-center bg-muted p-4'>
      <div className='max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center'>
        <div className='text-destructive mb-4'>
          <Icons.Alert className='w-16 h-16 mx-auto' />
        </div>

        <h2 className='text-xl font-bold text-foreground mb-2'>{t('routeError.title')}</h2>

        <p className='text-muted-foreground mb-4'>{t('routeError.description')}</p>

        {import.meta.env.DEV && (
          <details className='text-left text-xs bg-muted p-3 rounded mb-4'>
            <summary className='cursor-pointer font-medium'>{t('routeError.details')}</summary>
            <pre className='mt-2 overflow-auto'>
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          </details>
        )}

        <div className='flex flex-col sm:flex-row gap-3'>
          {reset && (
            <Button variant='outline' onClick={reset} className='flex-1'>
              {t('routeError.tryAgain')}
            </Button>
          )}
          <Button onClick={() => (window.location.href = '/')} className='flex-1'>
            {t('routeError.goHome')}
          </Button>
        </div>
      </div>
    </div>
  );
};
